import { useEffect, useState } from "react";
import { generateConversationAutoDigestEmailConfigurable } from "@templates/conversation-auto-digest-configurable";
import { generateConversationDigestEmailConfigurable } from "@templates/conversation-digest-configurable";
import { generateConversationReportEmailConfigurable } from "@templates/conversation-report-configurable";
import { generateCustomerReportEmail } from "@templates/customer-report";
import { generateEscalationAlertEmailConfigurable } from "@templates/escalation-alert-configurable";
import { generateMeetingRecapEmail } from "@templates/meeting-recap-email-template";
import { generateViewReportEmailConfigurable } from "@templates/view-report-configurable";
import { EmailTemplateConfigV2, EmailTemplateType } from "@/types/email-templates";
import { GenieBranding } from "@/types/ui/branding";

/** Real data for the currently-selected genie/team, layered over the mock
 * conversation content below so the "Email Signature" block (and anything
 * else that reads name/description/branding) reflects what will actually
 * render — not a genie that's permanently unbranded. */
export interface EmailTemplatePreviewContext {
  agentName?: string | null;
  agentDescription?: string | null;
  agentBranding?: GenieBranding | null;
  teamName?: string | null;
  teamBranding?: GenieBranding | null;
}

// Mock data that mirrors the real conversation+agent shape so EVERY value
// block can render in the preview. Production data lives at
// `conversation.details.{topics,transcript,lead_info,goals,analysis_result}`
// and on `conversation.agent` — both surfaces are populated here.
const mockTopics = ["Pricing", "Features", "Integration", "Support"];
const mockTranscript = [
  { role: "user", message: "Hi, I'm interested in your product. What can you tell me?", timestamp: 5 },
  { role: "agent", message: "Great to meet you. We help businesses replace static docs with talkable AI agents. Want a quick walkthrough?", timestamp: 12 },
  { role: "user", message: "Yes, and what does it cost? I'm comparing a few options.", timestamp: 28 },
  { role: "agent", message: "Our standard plan is $99 a month. I'll send you a quote with the full breakdown by email today. You can also see pricing at https://helpgenie.ai/pricing.", timestamp: 41 },
  { role: "user", message: "Can it integrate with HubSpot and Slack?", timestamp: 76 },
  { role: "agent", message: "Yes, we plug into HubSpot, Slack, and 250 other tools. I'll schedule a follow-up call to walk through the integration setup. What time works next week?", timestamp: 92 },
  { role: "user", message: "Tuesday afternoon works. My email is john@example.com.", timestamp: 138 },
  { role: "agent", message: "Booked for Tuesday 2pm. I'll email confirmation with the meeting link. Anything else?", timestamp: 165 },
];
const mockLeadInfo = {
  full_name: "John Doe",
  name: "John Doe",
  email: "john@example.com",
  company: "Example Corp",
  phone: "(555) 123-4567",
};
const mockAnalysisResult = {
  sentiment_analysis: {
    overall_sentiment_score: 0.85,
    overall_sentiment_type: "positive",
    customer_satisfaction_score: 0.85,
  },
  talk_metrics: {
    agent_talk_percentage: 45,
    customer_talk_percentage: 55,
  },
  key_insights: [
    "Customer expressed high satisfaction with product functionality",
    "Technical integration was a key discussion point",
    "Budget constraints mentioned for current quarter",
  ],
  conversation_summary: [
    "Customer demonstrated strong interest in premium features",
    "Discussed flexible pricing and budget constraints",
    "Scheduled follow-up meeting for next week",
    "Technical integration requirements identified",
  ],
};

const MOCK_AGENT_DEFAULTS = {
  name: "Sales Assistant",
  description:
    "Friendly sales rep who knows our pricing and integrations inside out. Books demos, sends quotes, and answers product questions.",
};

function buildMockAgent(context?: EmailTemplatePreviewContext) {
  return {
    id: "123",
    name: context?.agentName || MOCK_AGENT_DEFAULTS.name,
    description: context?.agentDescription ?? MOCK_AGENT_DEFAULTS.description,
    url_name: "sales-assistant",
    phone: { number: "+1 555 123 4567" },
    saved_qr_codes: [
      {
        storage_url: "https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=https%3A%2F%2Fhelpgenie.ai%2Flive-agent%2Fsales-assistant",
      },
    ],
    memory_enabled: true,
    capture_leads: true,
    is_demo_genie: false,
    category: "sales",
    voice_label: "Olivia",
    created_at: new Date(Date.now() - 60 * 24 * 3600 * 1000).toISOString(),
    goal: {
      fields: [
        { key: "full_name", label: "Full Name", required: true },
        { key: "email", label: "Email", required: true },
        { key: "phone", label: "Phone", required: false },
        { key: "company", label: "Company", required: false },
        { key: "budget", label: "Budget Range", required: false },
      ],
    },
    // The real selected genie's branding — this is what the "Email Signature"
    // block actually reads. An unselected genie stays unbranded, same as a
    // genie that never configured branding at all.
    branding: context?.agentBranding || {},
  };
}

function buildMockConversationData(context?: EmailTemplatePreviewContext) {
  const mockAgent = buildMockAgent(context);
  return {
    conversation: {
      id: "preview-conversation-id",
      agent_id: "123",
      agent: mockAgent,
      elevenlabs_id: "conv-123",
      created_at: new Date().toISOString(),
      details: {
        duration: 245,
        topics: mockTopics,
        transcript: mockTranscript,
        lead_info: mockLeadInfo,
        goals: ["Booked a demo", "Sent quote", "Captured contact info"],
        analysis_result: mockAnalysisResult,
        summary:
          "Customer asked about pricing and integrations. Agent walked through the standard plan, promised a quote and a follow-up call on Tuesday 2pm, and captured the customer's email.",
      },
    },
    aiResult: mockAnalysisResult,
    topics: mockTopics,
    insights: mockAnalysisResult.key_insights,
    transcript: mockTranscript,
    timezone: "UTC",
  };
}

export const useEmailTemplatePreview = (
  templateType: EmailTemplateType,
  config: EmailTemplateConfigV2,
  redirectUrl?: string,
  context?: EmailTemplatePreviewContext,
) => {
  const [previewHtml, setPreviewHtml] = useState("");

  useEffect(() => {
    generatePreview();
  }, [config, templateType, redirectUrl, context]);

  const generatePreview = () => {
    try {
      let html = "";
      const mockAgent = buildMockAgent(context);
      const mockConversationData = buildMockConversationData(context);

      if (templateType === "conversation_report") {
        html = generateConversationReportEmailConfigurable(
          { ...mockConversationData, redirect_url: redirectUrl },
          config,
        );
      } else if (templateType === "meeting_recap") {
        html = generateMeetingRecapEmail(
          {
            agentName: mockAgent.name,
            agentDescription: mockAgent.description,
            agentBranding: context?.agentBranding,
            summary: "The team discussed attaching existing phone numbers to AI agents and explored options for international customer support. Next steps include feasibility research and a follow-up meeting.",
            keyTakeaways: [
              "Attaching existing phone numbers to agents is the primary task.",
              "International customers require cross-country support considerations.",
              "Feasibility discovery is the immediate next step.",
            ],
            keyInsights: [
              "Customers may be from multiple countries, requiring international compatibility.",
              "The objective is to provide a useful solution for customers.",
            ],
            actionPlan: [
              "Research and document the feasibility of attaching existing phone numbers to agents.",
              "Identify cross-country compatibility requirements and regulatory constraints.",
              "Schedule a follow-up meeting to review findings.",
            ],
            decisions: [],
            recommendations: [
              "Clarify ownership for each action item.",
              "Develop a concise options matrix with pros/cons for each approach.",
            ],
            attendees: ["Alice", "Bob"],
            sentimentScore: 0.5,
            inboxUrl: redirectUrl ?? "https://helpgenie.ai/genie-hub/conversations",
          },
          config,
        );
      } else if (templateType === "escalation_alert") {
        html = generateEscalationAlertEmailConfigurable(
          {
            ...mockConversationData,
            redirect_url: redirectUrl,
            escalationReason: "Customer asked to speak with a human about a billing dispute and expressed frustration.",
            escalationSeverity: "high",
          },
          config,
        );
      } else if (templateType === "conversation_digest") {
        html = generateConversationDigestEmailConfigurable(
          {
            agentName: mockAgent.name,
            agentDescription: mockAgent.description,
            agentBranding: context?.agentBranding,
            periodStart: new Date(Date.now() - 7 * 24 * 3600 * 1000).toISOString(),
            periodEnd: new Date().toISOString(),
            conversationCount: 18,
            conversations: [
              { id: "preview-conversation-id", createdAt: new Date().toISOString(), summary: mockConversationData.conversation.details.summary, needsAttention: false },
              { id: "preview-conversation-id-2", createdAt: new Date(Date.now() - 86400000).toISOString(), summary: "Customer asked for a refund and escalated to billing.", needsAttention: true },
            ],
            topics: mockTopics,
            sentimentBreakdown: { positive: 11, neutral: 5, negative: 2 },
            escalations: [
              { id: "preview-conversation-id-2", createdAt: new Date(Date.now() - 86400000).toISOString(), summary: "Customer asked for a refund and escalated to billing.", needsAttention: true },
            ],
            timezone: "UTC",
          },
          config,
        );
      } else if (templateType === "conversation_auto_digest") {
        html = generateConversationAutoDigestEmailConfigurable(
          {
            agentName: mockAgent.name,
            agentDescription: mockAgent.description,
            agentBranding: context?.agentBranding,
            periodStart: new Date(Date.now() - 24 * 3600 * 1000).toISOString(),
            periodEnd: new Date().toISOString(),
            needsAttention: [
              { id: "preview-conversation-id-2", createdAt: new Date(Date.now() - 3600000).toISOString(), summary: "Customer asked for a refund and escalated to billing.", severity: "high", topic: "Billing" },
            ],
            autoHandled: [
              { id: "preview-conversation-id", createdAt: new Date().toISOString(), summary: mockConversationData.conversation.details.summary, topic: "Support" },
              { id: "preview-conversation-id-3", createdAt: new Date(Date.now() - 5400000).toISOString(), summary: "Customer asked about integration options and got a walkthrough.", topic: "Integrations" },
            ],
            irrelevant: [
              { id: "preview-conversation-id-4", createdAt: new Date(Date.now() - 7200000).toISOString(), summary: "Test call with no real content." },
            ],
            recommendations: [
              "Multiple customers asked about refund policy — consider adding a clear refund policy page to the knowledge base.",
              "Several conversations mentioned HubSpot integration — highlight this capability earlier in the agent's greeting.",
            ],
            resolutionRate: 67,
            timezone: "UTC",
          },
          config,
        );
      } else if (templateType === "view_report") {
        html = generateViewReportEmailConfigurable(
          {
            viewName: "Sales Leads — This Week",
            periodStart: new Date(Date.now() - 7 * 24 * 3600 * 1000).toISOString(),
            periodEnd: new Date().toISOString(),
            rowCount: 24,
            takeaways:
              "24 new leads this week, up from 17 last week. Most came in through the pricing conversation flow. Two customers mentioned churn risk due to a competitor's lower pricing — worth a follow-up call.",
            flaggedRows: [
              { rowIdx: 4, reason: "Customer explicitly compared pricing with a competitor and hinted at switching.", conversationId: "preview-conversation-id" },
              { rowIdx: 11, reason: "Negative sentiment — customer reported a bug blocking their workflow.", conversationId: "preview-conversation-id-2" },
            ],
            timezone: "UTC",
            // No single genie owns a view report — team branding is the only
            // signature source, same as the real send-time renderer.
            teamBranding: context?.teamBranding,
            teamName: context?.teamName,
          },
          config,
        );
      } else {
        html = generateCustomerReportEmail(
          {
            conversation: mockConversationData.conversation,
            aiResult: mockConversationData.aiResult,
            sharedMedia: [
              {
                kind: "link",
                url: "https://helpgenie.ai/pricing",
                title: "Plans and pricing",
                description: "Compare what each plan includes and what it costs per month.",
              },
            ],
            timezone: mockConversationData.timezone,
            redirect_url: redirectUrl,
          },
          config,
        );
      }
      setPreviewHtml(html);
    } catch (error) {
      console.error("Failed to generate preview:", error);
    }
  };

  return { previewHtml };
};
